import {
  ExegesisSlateBlock,
  ICreateLinkParams,
  IDeleteBlocksParams,
  IDeleteLinkParams,
  IInsertPrivateContentParams,
  INoteByIdResult,
  IUpdateBlocksParams,
  OverrideSParams,
  OverrideSResult,
} from '@rhizome/pgtypes';
import {
  BLOCK_REFERENCE_MENTION,
  convertSlateNodeToDBRow,
  createEditorMap,
  CreateNewPrivateContentRow,
  diffSlateContents,
  getTextFromNode,
  slateToDB,
} from '@rhizome/shared-slate-utils';
import { Plate, TNode } from '@udecode/plate-core';
import { MentionCombobox } from '@udecode/plate-ui-mention';
import { queryClient } from 'apps/exegesis/pages/_app';
import { useMutationHook } from 'apps/exegesis/utils/DataFetchers';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import styled from 'styled-components';
import { auth } from '../../config/firebase';
import {
  RenderBlockReferences,
  useBlockReferences,
  useTagsCombobox,
} from './ComboboxHelpers';
import { MainEditorToolbar } from './MainEditorToolbar';
import NoteControls from './NoteControls';
import { NoteIdContext } from './SlateEditorWrapper';
import { mainEditorPlugins } from './usedPlugins';

const SlateEditor = (props: {
  initialValue: ExegesisSlateBlock[];
  autocompleteTags: string[];
  editorId: string;

  title: string | undefined;
  published: boolean | undefined;
  noteTags: string[];
  author: string | undefined;

  noteFetching: boolean;
  lastEditedDate: string;
}) => {
  const noteId = useContext(NoteIdContext);
  // editor state
  const editorState = useRef(props.initialValue as TNode<any>[]);
  const prevEditorMap = useRef(
    createEditorMap(props.initialValue).newEditorMap
  );
  const prevLinkSet = useRef(createEditorMap(props.initialValue).linkSet);

  const updateInterval = useRef(null as any);

  /*
  this flag tells us whether the update has flushed 

  e.g. that there aren't any in-progress updates being flushed
  */
  const updateFlushed = useRef(true);

  /*
  this flag tells us whether any changes have been made

  e.g. we need to flush an update because editor state has changed
  Because this is set to true even if caret position changes, we need to perform additional checks in
  the flushToServer. this isn' the cleanest, may want to update this in the future
  */
  const updateToFlush = useRef(false);

  const [user] = useAuthState(auth);
  const tagComboboxItems = useTagsCombobox(props.autocompleteTags);
  const blockReferences = useBlockReferences(noteId);
  const { mutateAsync: updateNote } = useMutationHook<{
    params: [
      IDeleteBlocksParams[],
      OverrideSParams<IInsertPrivateContentParams>[],
      OverrideSParams<IUpdateBlocksParams>[],
      ICreateLinkParams[],
      IDeleteLinkParams[]
    ];
    result: void;
  }>({
    route: 'EditNote',
  });

  const flushToServer = useCallback(() => {
    // if all existing updates have flushed (nothing is currently in progress)
    // AND there are new updates to flush
    if (updateToFlush.current && updateFlushed.current) {
      updateToFlush.current = false;
      updateFlushed.current = false;
      const {
        inserted,
        updated,
        deleted,
        newMap,
        newLinks,
        deletedLinks,
        linkSet,
      } = diffSlateContents(
        editorState.current,
        prevEditorMap.current,
        prevLinkSet.current,
        user?.uid
      );
      // console.log(
      //   'diffmaps',
      //   inserted,
      //   updated,
      //   deleted,
      //   newMap,
      //   newLinks,
      //   deletedLinks
      // );

      // convert inserted and updated into their proper db row form
      const insertRows: OverrideSResult<IInsertPrivateContentParams>[] =
        inserted.map((el) => {
          const { newNode, tags } = convertSlateNodeToDBRow(el.slate_content);
          const textValue = getTextFromNode(el.slate_content);
          return CreateNewPrivateContentRow({
            slate_content: newNode,
            slate_parent: noteId,
            slate_index: el.slate_index,
            tags,
            block_id: el.block_id,
            creation_date: new Date(),
            last_edited_date: new Date(),
            fulltext: textValue,

            content_type: 'slate',
          });
        });
      const updateRows: OverrideSResult<IUpdateBlocksParams>[] = updated.map(
        (el) => {
          const { newNode, tags } = convertSlateNodeToDBRow(el.slate_content);
          const textValue = getTextFromNode(el.slate_content);
          return {
            slate_content: newNode,
            slate_index: el.slate_index,
            fulltext: textValue,
            tags,
            block_id: el.block_id,
            last_edited_date: new Date(),
            slide_content: null,
          };
        }
      );
      /*
          Only post an update if there's something to post!

          It seems like the updateToFlush flag should be enough for this, but it isn't, because changing the caret position seems
          to also trigger the onChange handler, which is what sets the updateToFlush flag to true. 

          It's possible that the update object might have a way for us to determine that a change only affects caret position.
          This could be a cleaner way to streamline updates in the future.
      */
      if (
        deleted.length > 0 ||
        insertRows.length > 0 ||
        updateRows.length > 0 ||
        deletedLinks.length > 0 ||
        newLinks.length > 0
      ) {
        updateNote([
          deleted.map((block_id) => {
            return {
              block_id,
            };
          }),
          insertRows,
          updateRows,
          newLinks,
          deletedLinks,
        ])
          .then(() => {
            updateFlushed.current = true;
            prevEditorMap.current = newMap;
            prevLinkSet.current = linkSet;
            // update cache!

            queryClient.setQueriesData<OverrideSResult<INoteByIdResult>[]>(
              ['NoteById', { note_id: noteId }],
              (oldData) => {
                if (!oldData) return [];
                const blockMap = new Map<
                  string,
                  OverrideSResult<INoteByIdResult>
                >();
                oldData.forEach((el) => {
                  blockMap.set(el.block_id, el);
                });

                const newState = slateToDB(editorState.current, noteId);
                const newStateWithUpdatedLinks: OverrideSResult<INoteByIdResult>[] =
                  newState.map((el) => {
                    // preserve note metadata
                    const newBlock: OverrideSResult<INoteByIdResult> = {
                      ...(blockMap.get(el.block_id) || oldData[0]),
                      ...el,
                    };
                    const linksAddedToThisBlock: string[] = newLinks.reduce<
                      string[]
                    >((acc, link) => {
                      if (link.private_content_2 === el.block_id) {
                        return [...acc, link.private_content_2!];
                      } else {
                        return acc;
                      }
                    }, []);
                    newBlock.backlinkarray?.push(...linksAddedToThisBlock);
                    const linksRemovedFromThisBlock = new Set(
                      deletedLinks.reduce<string[]>((acc, link) => {
                        if (link.private_content_2 === el.block_id) {
                          return [...acc, link.private_content_2!];
                        } else {
                          return acc;
                        }
                      }, [] as string[])
                    );
                    newBlock.backlinkarray =
                      newBlock.backlinkarray?.filter(
                        (el) => !linksRemovedFromThisBlock.has(el)
                      ) || ([] as string[]);
                    return newBlock;
                  });
                return newStateWithUpdatedLinks;
              }
            );
          })
          .catch((error) => {
            /*
                    if the update fails, we should still mark updateFlushed as true
                    also updateToFlush to true, because we still have changes pending.
                    however, we will not update the maps

                    to flesh this out a bit more:
                    if the message NEVER reached the server at all, we need to mark updateFlushed as true
                    if the message was sent, but the server returned an error, we still mark 
                    updateflushed as true, because the db tx ensures that db state will not change
                    if there's an error of any sort.

                    now the question is how to prevent a db deadlock - how to prevent repeatedly trying
                    to perform the same doomed operation.
                    */
            // our update has flushed, there is no in progress update. you can send another update
            updateFlushed.current = true;
            // however, there was an error, so there is still an existing update to flush
            // if this isn't set to true, then the program will never retry the update in the case of
            // a lost connection or server error
            updateToFlush.current = true;
          });
      } else {
        /*
        This block runs in case all the update parameters are empty (in which case there's no need to flush an update)
        However - we still need to reset the flags so that updates will flush in the future - otherwise the program will
        think we're still waiting on a server response
        */
        // our update has flushed, there is no in progress update
        updateFlushed.current = true;
        // set the editorMap (should be the same technically, but who knows)
        prevEditorMap.current = newMap;
        // same as above
        prevLinkSet.current = linkSet;
        // updateToFlush set to false at start of this function, keep it that way
      }
    }
  }, [noteId, updateNote, user?.uid]);

  // bind the server flushing function on an interval
  useEffect(() => {
    updateInterval.current = window.setInterval(flushToServer, 500);
    return () => {
      window.clearInterval(updateInterval.current);
    };
  }, [flushToServer]);

  return (
    <Plate
      id={noteId}
      plugins={mainEditorPlugins}
      editableProps={{
        placeholder: 'Write something...',
        spellCheck: true,
        autoFocus: true,
      }}
      initialValue={props.initialValue}
      onChange={(value: TNode<any>[]) => {
        console.log('main editor value', value);
        editorState.current = value;
        updateToFlush.current = true;
      }}
    >
      <MainEditorToolbar editorId={props.editorId} />
      <NoteControls
        title={props.title}
        published={props.published || false}
        noteTags={props.noteTags}
        author={props.author || ''}
      />
      <MentionCombobox items={tagComboboxItems} />

      <MentionCombobox
        items={blockReferences}
        pluginKey={BLOCK_REFERENCE_MENTION}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore this function call works lol
        onRenderItem={RenderBlockReferences}
        // component={BlockReferenceMenu}
      />
    </Plate>
  );
};

const OpacityWrapper = styled.div<{ noteFetching: boolean }>`
  opacity: ${(props) => (props.noteFetching ? 0.5 : 1)};
`;

export default SlateEditor;
